import * as angular from 'angular';
import * as angularTranslate from 'angular-translate';
import * as angularAnimate from 'angular-animate';
import * as angularMaterial from 'angular-material';
import { _LTracker as loggly } from 'loggly-jslogger';

import { detectModule } from '../detect';
import { goBannerModule } from '../go-banner';
import { allSettledModule } from '../all-settled';
import { announcementModelModule } from '../models/announcement';
import { animateProviderConfig } from './animate.provider.config';
import { unhandledRejectionConfig } from './unhandled-rejection.config';
import { goOfflineModeModule } from '../go-offline-mode';
import { FetchInterceptor } from './fetch-interceptor';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { ngxFeatureFlagServiceModule } from '@go-modules/services/feature-flag';
import { ngxUserpilotServiceModule } from '@go-modules/services/userpilot';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { updateManagerModule } from 'go-modules/update-manager';
import { UpdateManagerService } from 'go-modules/update-manager/update-manager.service';
import { goLocalizeHelperModule } from 'go-modules/translation-helper';
import { sessionManager } from 'go-modules/session-manager';
import { FullstoryProvider } from 'go-modules/services/fullstory/fullstory.service';
import { ENVIRONMENT_CONSTANTS } from 'go-modules/constants/environment-constants.constant';
import { fullstoryModule } from 'go-modules/services/fullstory';
import type { SessionManagerService } from 'go-modules/session-manager/session-manager.service';

const getAbsoluteUrl = (input: RequestInfo | URL): string => {
	if (input instanceof URL) {
		return input.toString();
	}
	return new URL(input instanceof Request ? input.url : input, document.baseURI).toString();
};

// Method for adding header for inteceptors
const interceptForFeatureFlags = <I extends RequestInfo | URL, C extends RequestInit | ng.IRequestConfig>(
	featureFlag: NgxFeatureFlagService, input: I, config?: C
): [I, C] => {
	const url = getAbsoluteUrl(input);

	if (!url.startsWith(getAbsoluteUrl(clientSettings.GoReactV2API)) &&
		!url.startsWith(getAbsoluteUrl(clientSettings.GoReactV1API))) {
		return [input, config];
	}


	const flags = featureFlag.getSynchronizedFlags();
	if (flags.length > 0) {
		config.headers = config.headers ?? {};
		config.headers['Goreact-Active-Feature-Flags'] = flags.join(',');
	}
	return [input, config];
};

// Url lives outside the fetch request object, so if there's a url, it has to be ng1
const isNgRequest = (config: RequestInit | ng.IRequestConfig): config is ng.IRequestConfig => {
	return 'url' in config;
};

// Method for adding withCredentials for interceptors
// Make sure that we are passing cookies back and forth:
//     The XMLHttpRequest.withCredentials property is a boolean value that indicates
//     whether or not cross-site Access-Control requests should be made using credentials
//     such as cookies, authorization headers or TLS client certificates.
// See https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/withCredentials
const interceptForWithCredentials = <I extends RequestInfo | URL, C extends RequestInit | ng.IRequestConfig>(
	input: I, config?: C
): [I, C] => {
	const url = getAbsoluteUrl(input);

	if (!url.startsWith(getAbsoluteUrl(clientSettings.GoReactV2API)) &&
		!url.startsWith(getAbsoluteUrl(clientSettings.GoReactV1API))) {
		return [input, config];
	}

	if (isNgRequest(config)) {
		config.withCredentials = config.withCredentials ?? true;
	} else {
		config.credentials = config.credentials ?? 'include';
	}

	return [input, config];
};

// Define a regular expression for URL validation
const sanitizationTrustedUrlRegex = new RegExp(
	'^\s*' +                                                                      // Anchor against the entire string, but allow any empty space before.
	'https?:\\/\\/' +                                                             // scheme
	'(?![^\\\\\\/?#]*@)' +                                                        // userInfo disallowed
	'(' +
		'((?:(?:[^\\\\\\/?#\\.]+?)\\.)*goreact\\.(?:com|eu|au)(?::[0-9]+)?)' + '|' + // goreact domain + port
		'((?:(?:[^\\\\\\/?#\\.]+?)\\.)*echo360\\.(?:com|eu|org))' + '|' +         // echo domain
		'((?:(?:[^\\\\\\/?#\\.]+?)\\.)*youtu(?:\\.be|be\\.com))' + '|' +          // youtube domain
		'((?:(?:[^\\\\\\/?#\\.]+?)\\.)*zoom\\.(?:com|us))' +                      // zoom domain
	')' +
	'(?=[\\\\\\/?#]|$)'                                                           // authority-terminating character.
);

// Adding config for $mdGestureProvider will fix <mat-checkbox> not triggering its change event
// The issue only occurs in Chrome-family browsers in mobile, issue does not occur in Safari
// Used this ticket with similar issue as reference: https://github.com/angular/components/issues/20725
angular.module('material.core.gestures')
	/** @ngInject */
	.config(($mdGestureProvider) => {
		$mdGestureProvider.disableAll();
	});

// This module exists for things we want to run
// on start up of goReact apps. For now this
// means LTI and Dashboard
export const appRunModule = angular.module('go.app-run', [
	goOfflineModeModule,
	detectModule,
	goBannerModule,
	sessionManager,
	angularTranslate,
	updateManagerModule,
	allSettledModule,
	announcementModelModule,
	angularAnimate,
	fullstoryModule,
	ngxFeatureFlagServiceModule,
	ngxUserpilotServiceModule,
	goLocalizeHelperModule,
	angularMaterial
])
	.config(animateProviderConfig)
	.config(unhandledRejectionConfig)

	/** @ngInject */
	.config(($httpProvider: ng.IHttpProvider) => {
		// Setup angular.js interceptor

		/** @ngInject */
		$httpProvider.interceptors.push((
			featureFlag: NgxFeatureFlagService
		): ng.IHttpInterceptor => {
			return {
				request: (config: ng.IRequestConfig) => {
					let [, newConfig] = interceptForFeatureFlags(featureFlag, config.url, config);
					[, newConfig] = interceptForWithCredentials(config.url, newConfig);
					return newConfig;
				}
			};
		});
	})
	.config(($compileProvider: ng.ICompileProvider) => {
		$compileProvider.imgSrcSanitizationTrustedUrlList(sanitizationTrustedUrlRegex);
		$compileProvider.aHrefSanitizationTrustedUrlList(sanitizationTrustedUrlRegex);
	})
	.config(
		function ($sceDelegateProvider: ng.ISCEDelegateProvider) {
			$sceDelegateProvider.resourceUrlWhitelist([
				'self',
				clientSettings.GoReactV2API + '/**'
			]);
		}
	)
	.config((fullstoryServiceProvider: FullstoryProvider) => {
		fullstoryServiceProvider.init(clientSettings.FullstoryOrgId);
	})
	.run((featureFlag: NgxFeatureFlagService, GoVersionManager: UpdateManagerService) => {
		// Setup fetch interceptor
		FetchInterceptor.install();
		FetchInterceptor.register({request: interceptForFeatureFlags.bind(null, featureFlag)});
		FetchInterceptor.register({request: interceptForWithCredentials});

		GoVersionManager.init();

		// Setup Loggly
		loggly.push({
			logglyKey: 'key',
			sendConsoleErrors: false,
			tag: `frontend-js-logs-${ENVIRONMENT_CONSTANTS.APP_ENVIRONMENT}-${ENVIRONMENT_CONSTANTS.APP_REGION}`,
			useDomainProxy: true
		});

	})
	.run((sessionManager: SessionManagerService) => {
		sessionManager.startVisibilityWatcher();
	})
	.name;
